import React, {
  FC,
  useState,
  useRef,
  ReactNode,
  PropsWithChildren,
  LegacyRef,
  MouseEventHandler,
  KeyboardEventHandler
} from 'react';
import classnames from 'classnames';

import { ComposedTheme, useTheme } from '@glu/theming';
import { useTrapFocus } from '@glu/utilities-react';

import Fade from 'components/transitions/Fade';
import useModalFocus from 'hooks/useModalFocus';
import useKeyHandler from 'hooks/useKeyHandler';
import useStyles from './Modal.styles';

interface Props {
  closeButton?: boolean;
  transition?: ReactNode | string | unknown;
  footer?: ReactNode | string;
  header?: ReactNode | string;
  headerStyles?: string;
  mountOnEnter?: boolean;
  onHide?: (evt: unknown) => void;
  show?: boolean;
  unmountOnExit?: boolean;
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error';
}

export const ModalComponent: FC<PropsWithChildren<Props>> = ({
  children,
  closeButton = true,
  footer = null,
  header = null,
  headerStyles = null,
  mountOnEnter = true,
  onHide = () => {},
  show = false,
  transition: Transition = Fade,
  transition,
  unmountOnExit = true,
  variant = 'default'
}) => {
  const classes = useStyles();
  const {
    transitions: {
      duration: { short }
    }
  } = useTheme<ComposedTheme>();
  const [transitionIn, setTransitionIn] = useState(true);
  const modalRef = useRef<HTMLElement>();

  const transitionDone = (event: unknown) => {
    setTransitionIn(true);
    onHide(event);
  };

  const closeModal: MouseEventHandler<HTMLDivElement | HTMLButtonElement> = (event) => {
    if (typeof transition === 'string') {
      onHide(event);
      return;
    }

    setTransitionIn(false);
    setTimeout(() => transitionDone(event), short);
  };

  const { backdrop, content, dialog, header: modalHeader, modal, modalIn } = classes;

  const showTransition = show && transitionIn;
  const styles = classes[variant as keyof typeof classes];

  /** @description Remove the props when a '<div>' is supplied. */
  let transitionProps = {
    in: show && transitionIn,
    mountOnEnter,
    unmountOnExit
  };

  /** @description Remove the props when a '<div>' is supplied. */
  if (typeof Transition === 'string') {
    transitionProps = {};
  }

  const getAriaLabel = () => 'Dialog';

  // Focus modal on open, focus previous element on close
  useModalFocus({ ref: modalRef, active: show });

  // Escape key to close modal
  useKeyHandler({ keyEvent: 'keyup', keyCode: 27, callback: closeModal });

  const onKeyDown = useTrapFocus({ ref: modalRef, active: transitionIn });

  return (
    <section className="ModalComponent">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <section
        ref={modalRef as LegacyRef<HTMLElement>}
        onKeyDown={onKeyDown as unknown as KeyboardEventHandler<HTMLElement>}
        tabIndex={-1}
        className={classnames('modal', modal, { [modalIn]: show })}
        role="dialog"
        aria-label={getAriaLabel()}
      >
        <Fade in={showTransition} mountOnEnter={mountOnEnter} unmountOnExit={unmountOnExit}>
          <div className={backdrop} onClick={closeModal} />
        </Fade>
        <Transition {...transitionProps}>
          <div className={classnames('dialog', dialog)} role="document">
            <section className={content}>
              {header && (
                <article className={`${modalHeader} ${styles} ${headerStyles || ''}`}>
                  {closeButton && (
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                      onClick={closeModal}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  )}
                  {header}
                </article>
              )}
              <article className="modal-body">{children}</article>
              {footer && <article className="modal-footer">{footer}</article>}
            </section>
          </div>
        </Transition>
      </section>
    </section>
  );
};

export default ModalComponent;
